import Accordion from "@components/HourlyMain/Accordion";
import LayoutGrid from "@components/HomeMain/LayoutGrid";
import { homeStateSelector, setHomeState } from "@store/redux/modules/home";
import { useDispatch, useSelector } from "react-redux";
import { fetchDataMap } from "@/components/BasicLayout/BasicHeader/Navigation";
import { refreshUserLocation } from "@/components/BasicLayout/BasicHeader/LocationMask";
import { useActivate, useUnactivate } from "react-activation";
import RainProb from "@assets/icons/Rain_Prob.svg?react";
import { isTestLink, pxChangeVw } from "@/utils";
import { appThemeStyle } from "@/config";
import { Skeleton } from "antd-mobile";
import ErrorPage from "@/components/ErrorPage";
import lodash from "lodash";

interface EarlyWarningProps {
  color?: string;
  fillColor?: string;
  bgColor?: string;
  size?: string;
  title: string;
  content: string;
  render?: () => React.ReactNode;
  [propName: string]: any;
}
export function EarlyWarning({
  color = "rgba(246, 15, 8, 1)",
  fillColor = "rgba(255, 83, 78, 1)",
  bgColor = "rgba(105, 0, 0,0.25)",
  size = "20px",
  title,
  content,
  render,
  ...args
}: EarlyWarningProps) {
  render = render ? render : () => <SvgIcon name="arrow_right" />;
  return (
    <div
      className="font-500 text-12 w-full  flex justify-between items-center p-[8px_16px] rounded-8"
      style={{ backgroundColor: bgColor }}
      {...args}
    >
      <div className="flex justify-start items-center gap-x-16">
        <AlertSvg color={fillColor} size={size} />
        <div className="flex flex-col items-start gap-y-5">
          <div className="font-bold" style={{ color }}>
            {title}
          </div>
          <div className="color-[#fff] font-300">{content}</div>
        </div>
      </div>
      {render && render()}
    </div>
  );
}
interface HourlyDefaultProps {}

interface AccordionItem {
  Hour: string;
  Temperature: { Value: number; Unit: string };
  WeatherIconImg: string;
  WeatherText: string;
  PrecipitationProbability: number;
  isExpand: boolean;
  warnings?: Array<{
    Description: { Localized: string };
    detail: { start: string; end: string };
    Color: { Name: string };
  }>;
  Items?: any[];
}
const HourlyDefault: React.FC<HourlyDefaultProps> = () => {
  const homeState = useSelector(homeStateSelector);
  const [hourList, setHourList] = useState<AccordionItem[]>([]);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const localSelectCity = LocalStorage.getItem("select_city");
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fetchData = useCallback(
    async (keyId?: string) => {
      try {
        const resData = await hourlyApi.query({
          test: isDevEnv ? 1 : undefined,
          keyId: keyId || localSelectCity?.Key || homeState?.info?.key || LocalStorage.getItem("info")?.key,
        });
        if (resData?.data?.hourList?.length) {
          const updatedHourList = resData.data.hourList.map((item: any) => ({
            ...item,
            isExpand: false,
          }));
          LocalStorage.setItem("info", resData.data.baseInfo);
          dispatch(setHomeState({ info: resData.data.baseInfo }));
          setHourList(() => updatedHourList);
        }
      } catch (error: any) {
        setIsShowErrorPage(true);
        setErrorMessage(error.message);
      }
    },
    [dispatch, homeState?.info?.key, localSelectCity?.Key]
  );

  useEffect(() => {
    fetchDataMap[pathname] = fetchData;
  }, [fetchData, pathname]);

  useEffect(() => {
    if (!(homeState?.info?.key || LocalStorage.getItem("info")?.key || localSelectCity?.Key)) {
      refreshUserLocation(dispatch);
    } else {
      fetchData();
    }
  }, [dispatch, fetchData, homeState?.info?.key, localSelectCity?.Key]);

  useActivate(() => {
    console.log("Hourly组件已激活");
  });
  useUnactivate(() => {
    console.log("Hourly组件已缓存");
  });
  const weatherStatus = homeState?.info?.weatherStatus || LocalStorage.getItem("info")?.weatherStatus;
  const tipsStyle = appThemeStyle?.[weatherStatus]?.tipsStyle ?? {};

  const renderTitle = useCallback(
    (item: AccordionItem, index: number, handleClick: (item: any, index: number) => void) => (
      <div className="flex justify-between items-center text-12" onClick={() => handleClick(item, index)}>
        <div style={{ flex: 1.5 }}>{item.Hour}</div>
        <div style={{ flex: 1, fontSize: pxChangeVw("16px") }}>
          {item?.Temperature?.Value + item?.Temperature?.Unit}
        </div>
        <div style={{ flex: 4 }} className="flex items-center gap-x-5">
          <WeatherImage className="w-20" src={item?.WeatherIconImg} />
          <div>{item.WeatherText}</div>
        </div>
        <div style={{ flex: 2 }} className="flex items-center gap-x-5">
          <RainProb />
          <div>{item?.PrecipitationProbability}%</div>
        </div>
        <div className="center flex-[1.5]">
          <SvgIcon name={item?.isExpand ? "arrow_up" : "arrow_down"} />
        </div>
      </div>
    ),
    []
  );

  const renderContent = useCallback(
    (item: AccordionItem) => (
      <div className="flex flex-col gap-y-8">
        {!!item?.warnings?.length &&
          item.warnings.map((warningItem: any, warningIndex: number) => {
            const warning =
              appThemeStyle?.[weatherStatus]?.warning?.[warningItem?.Color?.Name?.toLocaleLowerCase()] ?? {};
            return (
              <EarlyWarning
                key={warningIndex}
                title={warningItem.Description?.Localized}
                content={warningItem?.detail?.start + "-" + warningItem?.detail?.end}
                color={warning?.fontColor}
                bgColor={tipsStyle?.background}
                fillColor={warning?.fillColor}
              />
            );
          })}
        {!!item?.Items?.length && (
          <LayoutGrid
            list={item.Items}
            style={{ background: "transparent", backdropFilter: "none", boxShadow: "none" }}
          />
        )}
      </div>
    ),
    [weatherStatus, tipsStyle]
  );

  return isShowErrorPage ? (
    <ErrorPage
      errorMessage={errorMessage}
      refreshPage={() => {
        setIsShowErrorPage(() => false);
        fetchData();
      }}
    />
  ) : (
    <div className="flex flex-col justify-start gap-y-10 p-[0_16px] box-border">
      {(isTestLink([...domainMap["wi"], ...domainMap["wg"]]) ||
        [...domainMap["wi"], ...domainMap["wg"]].includes(window.location.hostname)) && (
        <GoogleAd {...useGoogleAd("320x100", 0)} />
      )}
      {(isTestLink(lodash.flatten(Object.values(lodash.omit(domainMap, ["wi"])))) ||
        lodash.flatten(Object.values(lodash.omit(domainMap, ["wi"]))).includes(window.location.hostname)) && (
        <GoogleAd {...useGoogleAd("300x250", 2)} />
      )}
      {isEmptyType(hourList) ? (
        <>
          <Skeleton
            animated
            style={{
              "--width": "100%",
              "--height": "901px",
              "--border-radius": "10px",
            }}
          />
          {Array(23)
            .fill(undefined)
            .map((_item, index) => (
              <Skeleton
                key={index}
                animated
                style={{
                  "--width": "100%",
                  "--height": "52px",
                  "--border-radius": "10px",
                }}
              />
            ))}
        </>
      ) : (
        <Accordion list={hourList} setLocalList={setHourList} renderTitle={renderTitle} renderContent={renderContent} />
      )}
    </div>
  );
};
export default HourlyDefault;
